import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { GET_ORDER_HISTORY_BY_CLUB_ID } from '../graphql/orderHistoryModel/orderHistoryQuery';
import { GET_REDEEM_SERVICE_HISTORY_BY_CLUB_ID, REDEEM_SERVICE_HISTORY_UPDATED } from '../graphql/redeemServiceHistoryModel/redeemServiceHistoryQuery';
import { ClubDetailsContext } from './ClubDetailsContext';
import dayjs from 'dayjs';
import { fetchNextPage, PAGE_SIZE } from '../utils/pagination';

export const HistoryContext = createContext({});

export const HistoryProvider = ({ children }) => {
    const { clubDetails } = useContext(ClubDetailsContext);

    const [orderTableData, setOrderTableData] = useState([]);
    const [redeemTableData, setRedeemTableData] = useState([]);
    const [hasMoreHistory, setHasMoreHistory] = useState({ order: false, redeem: false });
    const [loadingMoreHistory, setLoadingMoreHistory] = useState({ order: false, redeem: false });
    const [historyOffsets, setHistoryOffsets] = useState({ order: 0, redeem: 0 });
    const [paginationError, setPaginationError] = useState(null);

    // Date range state — defaults to empty (show all)
    const [dateRange, setDateRange] = useState({
        startDate: null,
        endDate: null,
    });

    const { loading: orderLoading, error: orderError, data: orderData, fetchMore: fetchMoreOrders } = useQuery(GET_ORDER_HISTORY_BY_CLUB_ID, {
        variables: { clubId: clubDetails?.id, limit: PAGE_SIZE, offset: 0 },
        skip: !clubDetails?.id,
    });

    const {
        loading: redeemLoading,
        error: redeemError,
        data: redeemData,
        subscribeToMore,
        fetchMore: fetchMoreRedemptions,
    } = useQuery(GET_REDEEM_SERVICE_HISTORY_BY_CLUB_ID, {
        variables: { clubId: clubDetails?.id, limit: PAGE_SIZE, offset: 0 },
        skip: !clubDetails?.id,
    });

    const isLoading = orderLoading || redeemLoading;

    useEffect(() => {
        setOrderTableData([]);
        setRedeemTableData([]);
        setHasMoreHistory({ order: false, redeem: false });
        setLoadingMoreHistory({ order: false, redeem: false });
        setHistoryOffsets({ order: 0, redeem: 0 });
        setPaginationError(null);
    }, [clubDetails?.id]);

    // Error logging
    useEffect(() => {
        if (orderError || redeemError) {
            console.error('Error fetching history data:', orderError || redeemError);
        }
    }, [orderError, redeemError]);

    // Set table data
    useEffect(() => {
        if (orderData?.getOrderHistoryByClubId) {
            setOrderTableData(orderData.getOrderHistoryByClubId);
            if (historyOffsets.order === 0) {
                setHistoryOffsets(current => ({ ...current, order: orderData.getOrderHistoryByClubId.length }));
                setHasMoreHistory(current => ({
                    ...current,
                    order: orderData.getOrderHistoryByClubId.length === PAGE_SIZE,
                }));
            }
        }
    }, [historyOffsets.order, orderData]);

    useEffect(() => {
        if (redeemData?.getRedeemServiceHistoryByClubId) {
            setRedeemTableData(redeemData.getRedeemServiceHistoryByClubId);
            if (historyOffsets.redeem === 0) {
                setHistoryOffsets(current => ({ ...current, redeem: redeemData.getRedeemServiceHistoryByClubId.length }));
                setHasMoreHistory(current => ({
                    ...current,
                    redeem: redeemData.getRedeemServiceHistoryByClubId.length === PAGE_SIZE,
                }));
            }
        }
    }, [historyOffsets.redeem, redeemData]);

    const loadMoreOrders = useCallback(async () => {
        if (!hasMoreHistory.order || loadingMoreHistory.order || !clubDetails?.id) return;
        setLoadingMoreHistory(current => ({ ...current, order: true }));
        try {
            const received = await fetchNextPage({
                fetchMore: fetchMoreOrders,
                field: 'getOrderHistoryByClubId',
                items: orderTableData,
                offset: historyOffsets.order,
                variables: { clubId: clubDetails.id },
            });
            setHistoryOffsets(current => ({ ...current, order: current.order + received }));
            setHasMoreHistory(current => ({ ...current, order: received === PAGE_SIZE }));
        } catch (error) {
            setPaginationError(error);
            setHasMoreHistory(current => ({ ...current, order: false }));
        } finally {
            setLoadingMoreHistory(current => ({ ...current, order: false }));
        }
    }, [clubDetails?.id, fetchMoreOrders, hasMoreHistory.order, historyOffsets.order, loadingMoreHistory.order, orderTableData]);

    const loadMoreRedemptions = useCallback(async () => {
        if (!hasMoreHistory.redeem || loadingMoreHistory.redeem || !clubDetails?.id) return;
        setLoadingMoreHistory(current => ({ ...current, redeem: true }));
        try {
            const received = await fetchNextPage({
                fetchMore: fetchMoreRedemptions,
                field: 'getRedeemServiceHistoryByClubId',
                items: redeemTableData,
                offset: historyOffsets.redeem,
                variables: { clubId: clubDetails.id },
            });
            setHistoryOffsets(current => ({ ...current, redeem: current.redeem + received }));
            setHasMoreHistory(current => ({ ...current, redeem: received === PAGE_SIZE }));
        } catch (error) {
            setPaginationError(error);
            setHasMoreHistory(current => ({ ...current, redeem: false }));
        } finally {
            setLoadingMoreHistory(current => ({ ...current, redeem: false }));
        }
    }, [clubDetails?.id, fetchMoreRedemptions, hasMoreHistory.redeem, historyOffsets.redeem, loadingMoreHistory.redeem, redeemTableData]);

    useEffect(() => {
        if (!subscribeToMore || !clubDetails?.id) return;

        const unsubscribe = subscribeToMore({
            document: REDEEM_SERVICE_HISTORY_UPDATED,
            variables: { clubId: clubDetails.id },
            updateQuery: (prev, { subscriptionData }) => {
                if (!subscriptionData.data) return prev;
                const newRecord = subscriptionData.data.redeemServiceHistoryUpdated;
                
                // Ignore events that were filtered out by the backend
                if (!newRecord) return prev;
                
                // If it already exists, replace it (e.g. status update)
                const previousRecords = prev?.getRedeemServiceHistoryByClubId || [];
                const exists = previousRecords.some(item => item.id === newRecord.id);
                if (exists) {
                    return Object.assign({}, prev, {
                        getRedeemServiceHistoryByClubId: previousRecords.map(item =>
                            item.id === newRecord.id ? newRecord : item
                        )
                    });
                }
                
                // If it's new, add it to the top
                return Object.assign({}, prev, {
                    getRedeemServiceHistoryByClubId: [newRecord, ...previousRecords]
                });
            }
        });

        return () => unsubscribe();
    }, [subscribeToMore, clubDetails?.id]);

    // Filter data by selected date range
    const filteredOrderData = useMemo(() => {
        return filterByDateRange(orderTableData, 'purchaseDate', dateRange);
    }, [orderTableData, dateRange]);

    const filteredRedeemData = useMemo(() => {
        return filterByDateRange(redeemTableData, 'redeemServiceRequestTime', dateRange);
    }, [redeemTableData, dateRange]);

    // Memoized summary data — computed from FILTERED data
    const orderSummary = useMemo(() => summarizeOrders(filteredOrderData), [filteredOrderData]);
    const redeemSummary = useMemo(() => groupRedeemDataByShelfAndAlcohol(filteredRedeemData), [filteredRedeemData]);

    return (
        <HistoryContext.Provider
            value={{
                orderSummary,
                orderTableData,
                redeemTableData,
                filteredOrderData,
                filteredRedeemData,
                redeemSummary,
                isLoading,
                hasMoreHistory,
                loadingMoreHistory,
                loadMoreOrders,
                loadMoreRedemptions,
                paginationError,
                dateRange,
                setDateRange,
            }}
        >
            {children}
        </HistoryContext.Provider>
    );
};

const summarizeOrders = (orderList) => {
    const result = {
        totalAmount: 0,
        shelfTotals: {}
    };

    for (let i = 0; i < orderList.length; i++) {
        const order = orderList[i];
        result.totalAmount += parseFloat(order.totalPartnerAmount) || 0;

        const details = order.orderDetails;
        for (let j = 0; j < details.length; j++) {
            const { shelf, partnerBundlePrice } = details[j];
            const price = parseFloat(partnerBundlePrice) || 0;

            if (!result.shelfTotals[shelf]) {
                result.shelfTotals[shelf] = 0;
            }

            result.shelfTotals[shelf] += price;
        }
    }

    return result;
};

const groupRedeemDataByShelfAndAlcohol = (redeemHistoryList) => {
    const result = {};

    for (let i = 0; i < redeemHistoryList.length; i++) {
        const redeemHistory = redeemHistoryList[i];
        if (redeemHistory.redeemServiceStatus?.toLowerCase() !== 'approved') {
            continue;
        }

        const details = redeemHistory.redeemServiceDetails;

        for (let j = 0; j < details.length; j++) {
            const { shelf, alcoholName, count } = details[j];
            const quantity = parseInt(count, 10) || 0;

            if (!result[shelf]) {
                result[shelf] = {};
            }

            if (!result[shelf][alcoholName]) {
                result[shelf][alcoholName] = 0;
            }

            result[shelf][alcoholName] += quantity;
        }
    }

    return result;
};

const filterByDateRange = (data, dateField, dateRange) => {
    const { startDate, endDate } = dateRange;
    if (!startDate && !endDate) return data;
    return data.filter(item => {
        const d = dayjs(item[dateField]);
        if (!d.isValid()) return false;
        if (startDate && d.isBefore(dayjs(startDate).startOf('day'))) return false;
        if (endDate && d.isAfter(dayjs(endDate).endOf('day'))) return false;
        return true;
    });
};
